/**
 * Ledger period selector — custom pills (Today default) + a custom
 * date-range control built from the shared custom DatePicker (no native
 * date input). Ruby on light, 44px targets.
 */
import React from 'react';
import { DatePicker } from '../../../components/DatePicker';
import { PERIOD_PRESETS, dayKey, type LedgerPeriod } from './ledgerModel';

export const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; ariaLabel?: string }> = ({
  active, onClick, children, ariaLabel,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={ariaLabel}
    className={`inline-flex items-center min-h-[44px] px-3 rounded-full text-[12px] font-semibold border transition-colors whitespace-nowrap ${
      active ? 'bg-[#DC143C] text-white border-[#DC143C]' : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
    }`}
  >
    {children}
  </button>
);

const LedgerPeriodPicker: React.FC<{ value: LedgerPeriod; onChange: (p: LedgerPeriod) => void }> = ({ value, onChange }) => {
  const today = dayKey(new Date());
  const startCustom = () => onChange({ preset: 'custom', fromDay: value.fromDay ?? today, toDay: value.toDay ?? today });
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Time period">
      {PERIOD_PRESETS.map(({ preset, label }) => (
        <Pill
          key={preset}
          active={value.preset === preset}
          onClick={() => (preset === 'custom' ? startCustom() : onChange({ preset }))}
        >
          {label}
        </Pill>
      ))}
      {value.preset === 'custom' && (
        <div className="flex items-center gap-2 text-stone-700" data-testid="ledger-custom-range">
          <div className="w-[150px]">
            <DatePicker
              value={value.fromDay ?? today}
              max={value.toDay ?? today}
              onChange={(d) => onChange({ ...value, fromDay: d })}
              placeholder="From"
              className="!h-11"
            />
          </div>
          <span className="text-[12px] text-stone-500">to</span>
          <div className="w-[150px]">
            <DatePicker
              value={value.toDay ?? today}
              min={value.fromDay}
              max={today}
              onChange={(d) => onChange({ ...value, toDay: d })}
              placeholder="To"
              className="!h-11"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerPeriodPicker;
